import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function formatEUR(value) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

// Plugin: schreibt Prozentwerte an die ROSI-Linie
const rosiValueLabelsPlugin = {
  id: "rosiValueLabelsPlugin",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const datasetIndex = chart.data.datasets.findIndex(
      (ds) => ds.label === "ROSI (%)"
    );

    if (datasetIndex === -1) return;

    const meta = chart.getDatasetMeta(datasetIndex);
    const values = chart.data.datasets[datasetIndex].data;

    ctx.save();
    ctx.font = "12px sans-serif";
    ctx.fillStyle = "rgba(54, 162, 235, 1)";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";

    meta.data.forEach((point, index) => {
      const value = values[index];
      ctx.fillText(`${value.toFixed(0)}%`, point.x, point.y - 8);
    });

    ctx.restore();
  },
};

const ROSIChart = () => {
  // Beispielwerte
  // avoidedLoss = vermiedener erwarteter Schaden / erwartete Einsparungen
  const measures = useMemo(
    () => [
      {
        name: "Firewall & IDS",
        cost: 20000,
        avoidedLoss: 50000,
      },
      {
        name: "Verschlüsselung",
        cost: 12000,
        avoidedLoss: 22000,
      },
      {
        name: "Awareness-Schulungen",
        cost: 8000,
        avoidedLoss: 18000,
      },
      {
        name: "Patch-Management",
        cost: 6000,
        avoidedLoss: 15000,
      },
    ],
    []
  );

  const rosiValues = measures.map(
    (m) => ((m.avoidedLoss - m.cost) / m.cost) * 100
  );

  const data = {
    labels: measures.map((m) => m.name),
    datasets: [
      {
        label: "Kosten der Maßnahme (€)",
        data: measures.map((m) => m.cost),
        backgroundColor: "rgba(254, 199, 72, 0.5)",
        borderColor: "rgba(254, 199, 72, 1)",
        borderWidth: 1,
      },
      {
        label: "Vermiedener erwarteter Schaden (€)",
        data: measures.map((m) => m.avoidedLoss),
        backgroundColor: "rgba(75, 192, 192, 0.5)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "ROSI (%)",
        data: rosiValues,
        type: "line",
        yAxisID: "y1",
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.5)",
        borderWidth: 2,
        tension: 0.2,
        pointRadius: 4,
        pointHoverRadius: 5,
      },
    ],
  };

  const maxROSI = Math.max(...rosiValues);

  const options = {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "EUR",
        },
      },
      y1: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        suggestedMax: maxROSI + 40,
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: "ROSI (%)",
        },
        ticks: {
          callback: function (value) {
            return `${value}%`;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (context.dataset.label === "ROSI (%)") {
              return `ROSI: ${context.raw.toFixed(1)}%`;
            }
            return `${context.dataset.label}: ${formatEUR(context.raw)}`;
          },
        },
      },
    },
  };

  return (
    <div className="container mt-5">
      <h4 className="text-center m-53">
        ROSI-Vergleich von Cybersecurity-Maßnahmen
      </h4>

      <p className="text-center" style={{ maxWidth: 900, margin: "0 auto 1rem" }}>
        Der Chart vergleicht die <strong>Kosten einer Maßnahme</strong> mit dem
        <strong> vermiedenen erwarteten Schaden</strong>. Die blaue Linie zeigt den
        <strong> ROSI in Prozent</strong>.
      </p>

      <Bar
        data={data}
        options={options}
        plugins={[rosiValueLabelsPlugin]}
      />

      <div className="mt-3">
        <details>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>
            Mini-Legende
          </summary>
          <ul className="mt-2" style={{ textAlign: "left" }}>
            <li>
              <strong>Kosten der Maßnahme:</strong> Investition in die Sicherheitsmaßnahme
            </li>
            <li>
              <strong>Vermiedener erwarteter Schaden:</strong> geschätzter finanzieller Schaden,
              der durch die Maßnahme verhindert wird
            </li>
            <li>
              <strong>ROSI:</strong> zeigt, wie wirtschaftlich die Maßnahme ist
            </li>
            <li>
              <strong>ROSI {">"} 0%:</strong> wirtschaftlich positiv
            </li>
            <li>
              <strong>ROSI = 0%:</strong> Kosten und vermiedener Schaden sind gleich hoch
            </li>
            <li>
              <strong>ROSI {"<"} 0%:</strong> rein wirtschaftlich sind die Kosten höher als der vermiedene Schaden
            </li>
          </ul>
        </details>
      </div>
    </div>
  );
};

export default ROSIChart;